import { Injectable } from '@angular/core';
import { Auth, signInAnonymously, User, onAuthStateChanged, signOut } from '@angular/fire/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageKey } from '../models/storageKey';

@Injectable({
  providedIn: 'root'
})
export class FirebaseAuthService {
  private currentUser$ = new BehaviorSubject<User | null>(null);
  private initializedSubject = new BehaviorSubject<boolean>(false);
  private initializationPromise: Promise<User | null> | null = null;

  constructor(private auth: Auth) {
    // Listen to auth state changes
    onAuthStateChanged(this.auth, (user) => {
      this.currentUser$.next(user);
      if (user && user.isAnonymous) {
        // Store anonymous UID for reuse
        localStorage.setItem(LocalStorageKey.ANONYMOUS_UID, user.uid);
      }
    });
  }

  /**
   * Initialize anonymous authentication
   * Checks for existing anonymous session first, creates new one if needed
   * Can be called multiple times safely - will return existing promise if already initializing
   */
  async initializeAnonymousAuth(): Promise<User | null> {
    // If already initializing, return the existing promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // If already initialized and have a user, return immediately
    if (this.initializedSubject.value && this.currentUser$.value) {
      return Promise.resolve(this.currentUser$.value);
    }

    // Create new initialization promise
    this.initializationPromise = this._performInitialization();

    try {
      const user = await this.initializationPromise;
      this.initializedSubject.next(true);
      return user;
    } finally {
      // Clear the promise so future calls can create a new one if needed
      this.initializationPromise = null;
    }
  }

  private async _performInitialization(): Promise<User | null> {
    try {
      // Check if we already have an authenticated user
      if (this.auth.currentUser) {
        console.log('Using existing authenticated user:', this.auth.currentUser.uid);
        return this.auth.currentUser;
      }

      // Check for stored anonymous UID
      const storedUid = localStorage.getItem(LocalStorageKey.ANONYMOUS_UID);
      if (storedUid) {
        console.log('Found stored anonymous UID:', storedUid);
        // The auth state listener will handle setting currentUser if still valid
        // Wait a bit for auth state to sync
        await new Promise(resolve => setTimeout(resolve, 500));

        const currentUser = this.auth.currentUser as User | null;
        if (currentUser?.uid === storedUid) {
          console.log('Reusing existing anonymous session');
          return currentUser;
        } else {
          console.log('Stored session expired, creating new anonymous user');
        }
      }

      // Create new anonymous user
      console.log('Creating new anonymous user');
      const credential = await signInAnonymously(this.auth);
      console.log('Anonymous user created:', credential.user.uid);
      return credential.user;

    } catch (error) {
      console.error('Error initializing anonymous auth:', error);
      // Don't throw - allow app to continue with localStorage only
      return null;
    }
  }

  /**
   * Get current user as Observable
   */
  getCurrentUser$(): Observable<User | null> {
    return this.currentUser$.asObservable();
  }

  /**
   * Get current user synchronously
   */
  getCurrentUser(): User | null {
    return this.currentUser$.value;
  }

  /**
   * Get current user ID (anonymous or permanent)
   */
  getCurrentUserId(): string | null {
    return this.currentUser$.value?.uid || null;
  }

  /**
   * Check if current user is anonymous
   */
  isAnonymous(): boolean {
    return this.currentUser$.value?.isAnonymous ?? false;
  }

  /**
   * Check if auth is initialized
   */
  isInitialized(): boolean {
    return this.initializedSubject.value;
  }

  /**
   * Get initialization status as Observable
   */
  getInitializedStatus$(): Observable<boolean> {
    return this.initializedSubject.asObservable();
  }

  /**
   * Sign out current user (anonymous or permanent)
   */
  async signOutUser(): Promise<void> {
    try {
      await signOut(this.auth);
      localStorage.removeItem(LocalStorageKey.ANONYMOUS_UID);
      this.currentUser$.next(null);
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  /**
   * Get or create anonymous user (convenience method)
   */
  async getOrCreateAnonymousUser(): Promise<User | null> {
    return this.initializeAnonymousAuth();
  }
}
